import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { Range, getTrackBackground } from "react-range";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper";
import Image from "next/image";
import Link from "next/link";
import Swal from "sweetalert2";

import styles from "./styles/style.module.scss";
import server from "@/src/api/server";
import { handleImageLink } from "../../helpers/functions";
import SarIcon from "../../../assets/images/svg/sar.svg";

import "swiper/css";
import PdfDownloadIcon from "../../../assets/images/svg/download.svg";
import ExpandIcon from "../../../assets/images/svg/expand.svg";
import { Col, Row } from "react-bootstrap";

const RequestSection = ({ countries }) => {
  const { formatMessage, locale } = useIntl();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [allPersonnels, setAllPersonnels] = useState([]);
  const [filteredPersonnels, setFilteredPersonnels] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedReligion, setSelectedReligion] = useState("");
  const [ageRange, setAgeRange] = useState([100]);
  const [selectedServantId, setSelectedServantId] = useState(null);
  const [swiperRef, setSwiperRef] = useState(null);
  const [expandedImage, setExpandedImage] = useState("");

  const religions = useMemo(
    () => [
      { value: "muslim", label: { ar: "مسلم", en: "Muslim" } },
      { value: "christian", label: { ar: "مسيحي", en: "Christian" } },
      { value: "jewish", label: { ar: "يهودي", en: "Jewish" } },
    ],
    [],
  );

  const getLocalizedValue = (field, fallback = "-") => {
    if (typeof field === "object" && field !== null) {
      return field?.[locale] || field?.ar || field?.en || fallback;
    }
    if (typeof field === "string") {
      return field || fallback;
    }
    return fallback;
  };

  const getNormalizedReligion = (religionField) => {
    const value = getLocalizedValue(religionField, "").toString().toLowerCase();

    if (
      value.includes("islam") ||
      value.includes("muslim") ||
      value.includes("اسلام") ||
      value.includes("مسلم")
    ) {
      return "muslim";
    }

    if (
      value.includes("christ") ||
      value.includes("مسيح") ||
      value.includes("نصر")
    ) {
      return "christian";
    }

    if (
      value.includes("jew") ||
      value.includes("يهود") ||
      value.includes("juda")
    ) {
      return "jewish";
    }

    return "";
  };

  const selectedServant = useMemo(
    () =>
      filteredPersonnels.find(
        (item) => `${item?.id}` === `${selectedServantId}`,
      ) ||
      allPersonnels.find((item) => `${item?.id}` === `${selectedServantId}`) ||
      null,
    [filteredPersonnels, allPersonnels, selectedServantId],
  );

  useEffect(() => {
    const fetchPersonnels = async () => {
      try {
        setLoading(true);
        const personnelsRes = await server.get("/personnels");
        const personnels = personnelsRes?.data?.data?.personnels || [];
        const availablePersonnels = personnels.filter(
          (item) => item?.available,
        );
        setAllPersonnels(availablePersonnels);
        setFilteredPersonnels(availablePersonnels);
      } catch (error) {
        setAllPersonnels([]);
        setFilteredPersonnels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPersonnels();
  }, []);

  const handleSearch = () => {
    const result = allPersonnels.filter((item) => {
      const matchesCountry =
        !selectedCountry || `${item?.country?.id}` === `${selectedCountry}`;
      const matchesReligion =
        !selectedReligion ||
        getNormalizedReligion(item?.religion) === selectedReligion;
      const itemAge = Number(item?.age) || 0;
      const matchesAge = itemAge >= 18 && itemAge <= ageRange[0];

      return matchesCountry && matchesReligion && matchesAge;
    });

    setFilteredPersonnels(result);

    if (
      selectedServantId &&
      !result.find((item) => `${item?.id}` === `${selectedServantId}`)
    ) {
      setSelectedServantId(null);
    }
  };

  const buildMessage = (person) => {
    return `اسم الخادمة: ${person?.name?.ar || person?.name?.[locale] || "-"}
الديانة: ${getLocalizedValue(person?.religion)}
كود الخادمة: ${person?.id || "-"}
الدولة: ${getLocalizedValue(person?.country?.name)}
العمر: ${person?.age || "-"}
اللغة: ${getLocalizedValue(person?.experience)}
سنوات الخبرة: ${person?.name?.experienceYears || "-"}
رسوم الاستقدام: ${person?.recruitmentFees || "-"}`;
  };

  const handlePdfDownload = async (item) => {
    const pdfUrl = item?.name?.file;

    const response = await fetch(pdfUrl);
    const blob = await response.blob();

    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `${item?.name?.ar}.pdf`;
    link.click();

    window.URL.revokeObjectURL(link.href);
  };

  const handleExpandImage = (item) => {
    const imageUrl = handleImageLink(item?.name?.pdfImage);
    if (!imageUrl) return;

    setExpandedImage(imageUrl);
  };

  const onSubmit = async (data) => {
    if (!selectedServant) {
      Swal.fire(
        "",
        locale === "ar"
          ? "اختر العاملة أولاً"
          : "Please select a servant first",
        "warning",
      );
      return;
    }

    const payload = {
      name: data?.name,
      phone: data?.phone,
      email: data?.email,
      message: buildMessage(selectedServant),
    };

    try {
      setSubmitting(true);
      await server.post("/requests", payload);
      reset();
      setSelectedServantId(null);
      Swal.fire(
        `${formatMessage({ id: "thanks" })}`,
        `${formatMessage({ id: "thankMsg" })}`,
        "success",
      );
    } catch (error) {
      Swal.fire(
        "",
        locale === "ar" ? "حدث خطأ أثناء الإرسال" : "Failed to submit request",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={styles.request}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col lg={12}>
            <div className="form-group">
              <label htmlFor="name">{formatMessage({ id: "name" })}</label>
              <input
                id="name"
                className="form-control"
                {...register("name", { required: true })}
              />
              {errors?.name && (
                <p className="error-hint">
                  {formatMessage({ id: "requiredField" })}
                </p>
              )}
            </div>
          </Col>
          <Col lg={6}>
            <div className="form-group">
              <label htmlFor="phone">{formatMessage({ id: "phone" })}</label>
              <input
                id="phone"
                className="form-control"
                maxLength={14}
                {...register("phone", {
                  required: true,
                  pattern: /^[0-9+]+$/,
                })}
              />
              {errors?.phone && (
                <p className="error-hint">
                  {formatMessage({ id: "requiredField" })}
                </p>
              )}
            </div>
          </Col>
          <Col lg={6}>
            <div className="form-group">
              <label htmlFor="email">{formatMessage({ id: "email" })}</label>
              <input
                id="email"
                type="email"
                className="form-control"
                {...register("email", { required: true })}
              />
              {errors?.email && (
                <p className="error-hint">
                  {formatMessage({ id: "requiredField" })}
                </p>
              )}
            </div>
          </Col>
          <Col lg={12}>
            <div className="personnels">
              <Row>
                <Col lg={6}>
                  <div className="form-group">
                    <label htmlFor="country">
                      {formatMessage({ id: "country" })}
                    </label>
                    <select
                      id="country"
                      className="form-control form-select"
                      value={selectedCountry}
                      onChange={(event) =>
                        setSelectedCountry(event.target.value)
                      }
                    >
                      <option value="">
                        {locale === "ar" ? "الكل" : "All"}
                      </option>
                      {countries?.map((country) => (
                        <option key={country?.id} value={country?.id}>
                          {country?.name?.[locale] ||
                            country?.name?.ar ||
                            country?.name?.en}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="form-group">
                    <label htmlFor="religion">
                      {formatMessage({ id: "religion" })}
                    </label>
                    <select
                      id="religion"
                      className="form-control form-select"
                      value={selectedReligion}
                      onChange={(event) =>
                        setSelectedReligion(event.target.value)
                      }
                    >
                      <option value="">
                        {locale === "ar" ? "الكل" : "All"}
                      </option>
                      {religions.map((religion) => (
                        <option key={religion.value} value={religion.value}>
                          {religion?.label?.[locale] || religion?.label?.ar}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>
                <Col lg={12}>
                  <div className="form-group">
                    <label htmlFor="ageRange">
                      {formatMessage({ id: "ageRange" })}
                    </label>
                    <Range
                      values={ageRange}
                      step={1}
                      min={18}
                      max={100}
                      onChange={(values) => setAgeRange(values)}
                      rtl={locale === "ar" ? true : false}
                      renderTrack={({ props, children }) => (
                        <div
                          className="trackWrap"
                          onMouseDown={props.onMouseDown}
                          onTouchStart={props.onTouchStart}
                        >
                          <div
                            ref={props.ref}
                            className={styles.track}
                            rtl={locale === "ar" ? true : false}
                            style={{
                              height: "6px",
                              width: "100%",
                              borderRadius: "5px",
                              background: getTrackBackground({
                                values: ageRange,
                                colors: ["#6c3d8e", "#ccc"],
                                min: 18,
                                max: 100,
                                rtl: locale === "ar" ? true : false,
                              }),
                              alignSelf: "center",
                            }}
                          >
                            {children}
                          </div>
                        </div>
                      )}
                      renderThumb={({ props, isDragged }) => (
                        <div
                          className="thumb"
                          {...props}
                          style={{
                            ...props.style,
                          }}
                        >
                          <div
                            className="thumbInner"
                            style={{
                              backgroundColor: isDragged ? "#6c3d8e" : "#fff",
                            }}
                          />
                        </div>
                      )}
                    />
                    <div className="ageRangeValues d-flex align-items-center justify-content-between mt-2">
                      <span>18 {formatMessage({ id: "year" })}</span>
                      <span>
                        {ageRange[0]} {formatMessage({ id: "year" })}
                      </span>
                      <span>100 {formatMessage({ id: "year" })}</span>
                    </div>
                  </div>
                </Col>
                <Col lg={12}>
                  <div className="form-group">
                    <button
                      type="button"
                      className="btn nw-btn search-btn"
                      onClick={handleSearch}
                    >
                      {locale === "ar" ? "بحث" : "Search"}
                    </button>
                  </div>
                </Col>
                <Col lg={12}>
                  <div className="form-group">
                    <div className={styles.cardsWrap}>
                      {loading ? (
                        <p className={styles.emptyText}>
                          {locale === "ar" ? "جاري التحميل..." : "Loading..."}
                        </p>
                      ) : filteredPersonnels.length === 0 ? (
                        <p className={styles.emptyText}>
                          {formatMessage({ id: "noPersonnels" })}
                        </p>
                      ) : (
                        <>
                          <Swiper
                            modules={[Navigation, Pagination]}
                            onSwiper={setSwiperRef}
                            slidesPerView={1}
                            spaceBetween={16}
                            breakpoints={{
                              768: { slidesPerView: 2 },
                              1024: { slidesPerView: 3 },
                            }}
                            pagination={{
                              dynamicBullets: true,
                              clickable: true,
                            }}
                            navigation={{
                              nextEl: ".navigationButtons .next",
                              prevEl: ".navigationButtons .prev",
                            }}
                          >
                            {filteredPersonnels.map((item) => (
                              <SwiperSlide key={item?.id}>
                                <div
                                  className={
                                    selectedServantId === item?.id
                                      ? "personnel active"
                                      : "personnel"
                                  }
                                >
                                  <div className="img">
                                    <Image
                                      src={handleImageLink(
                                        item?.name?.pdfImage,
                                      )}
                                      alt=""
                                      width={500}
                                      height={500}
                                    />
                                    <button
                                      type="button"
                                      className="pdf-btn"
                                      onClick={() => handlePdfDownload(item)}
                                    >
                                      <Image
                                        src={PdfDownloadIcon}
                                        alt=""
                                        width={16}
                                        height={16}
                                      />
                                      <FormattedMessage id="downloadPdf" />
                                    </button>
                                    <button
                                      type="button"
                                      className="expand-btn"
                                      onClick={() => handleExpandImage(item)}
                                    >
                                      <Image
                                        src={ExpandIcon}
                                        alt=""
                                        width={16}
                                        height={16}
                                      />
                                      <FormattedMessage id="expandImage" />
                                    </button>
                                  </div>
                                  <div className="info">
                                    <h3>
                                      <Link
                                        href={`/personnel/${item?.id}`}
                                        legacyBehavior
                                      >
                                        <a className="details-link nw-btn">
                                          {item?.name?.[locale]}
                                        </a>
                                      </Link>
                                    </h3>

                                    <ul>
                                      <li>
                                        <span className="key">
                                          <FormattedMessage id="nationality" />
                                          :{" "}
                                        </span>
                                        {item?.country?.name?.[locale]}
                                      </li>
                                      <li>
                                        <span className="key">
                                          <FormattedMessage id="code" />:
                                        </span>
                                        {item?.id}
                                      </li>

                                      <li>
                                        <span className="key">
                                          <FormattedMessage id="religion" />
                                          :{" "}
                                        </span>
                                        {item?.religion?.[locale]}
                                      </li>
                                      <li>
                                        <span className="key">
                                          <FormattedMessage id="experienceYears" />
                                          :{" "}
                                        </span>
                                        {item?.name?.experienceYears}
                                      </li>
                                      <li>
                                        <span className="key">
                                          <FormattedMessage id="age" />:
                                        </span>
                                        {item?.age}{" "}
                                        <FormattedMessage id="year" />
                                      </li>
                                      <li>
                                        <span className="key">
                                          <FormattedMessage id="job" />:
                                        </span>
                                        {item?.job?.[locale]}
                                      </li>
                                      {item?.salary && (
                                        <li>
                                          <span className="key">
                                            <FormattedMessage id="monthlySalary" />
                                            :
                                          </span>{" "}
                                          {item?.salary}
                                          <Image
                                            src={SarIcon}
                                            alt=""
                                            width={18}
                                            height={18}
                                            className="mx-1"
                                          />
                                        </li>
                                      )}
                                      {item?.recruitmentFees && (
                                        <li>
                                          <span className="key">
                                            <FormattedMessage id="recruitmentFees" />
                                            :
                                          </span>{" "}
                                          {item?.recruitmentFees}
                                          <Image
                                            src={SarIcon}
                                            alt=""
                                            width={18}
                                            height={18}
                                            className="mx-1"
                                          />
                                        </li>
                                      )}
                                    </ul>
                                    <div className="btns d-flex align-items-center justify-content-between gap-3">
                                      <button
                                        type="button"
                                        className="open-request-modal nw-btn"
                                        onClick={() =>
                                          setSelectedServantId(item?.id)
                                        }
                                      >
                                        <FormattedMessage id="selectServant" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </SwiperSlide>
                            ))}
                          </Swiper>

                          <div className="navigationButtons d-flex align-items-center justify-content-center gap-3 mt-4">
                            <button type="button" className="prev">
                              {locale === "ar" ? "‹" : "‹"}
                            </button>
                            <button type="button" className="next">
                              {locale === "ar" ? "›" : "›"}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
          <Col lg={12}>
            <div className="form-group">
              <button
                type="submit"
                className="btn nw-btn submit-btn"
                disabled={submitting}
              >
                {submitting
                  ? locale === "ar"
                    ? "جاري الإرسال..."
                    : "Submitting..."
                  : formatMessage({ id: "send" })}
              </button>
            </div>
          </Col>
        </Row>
      </form>
      {expandedImage && (
        <div className="zoom-image" onClick={() => setExpandedImage("")}>
          <Image src={expandedImage} width={1200} height={1200} alt="" />
        </div>
      )}
    </section>
  );
};

export default RequestSection;
